const PENDING_NAVIGATION_KEY = 'pending_navigation_analytics';

const isBrowser = typeof window !== 'undefined';
const posthogKey =
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN ||
  import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN;
const posthogHost =
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
const hasPostHogConfig = Boolean(posthogKey);

let analyticsClient = null;
let analyticsInitPromise = null;
let analyticsLoadScheduled = false;
let analyticsReady = false;
const pendingOperations = [];

function flushPendingOperations() {
  if (!analyticsClient) return;

  while (pendingOperations.length > 0) {
    const operation = pendingOperations.shift();
    operation?.(analyticsClient);
  }
}

function queueOperation(operation) {
  if (!hasPostHogConfig) return false;

  if (analyticsClient) {
    operation(analyticsClient);
    return true;
  }

  pendingOperations.push(operation);
  return true;
}

async function loadPostHogClient() {
  if (!isBrowser || !hasPostHogConfig) return null;
  if (analyticsClient) return analyticsClient;
  if (analyticsInitPromise) return analyticsInitPromise;

  analyticsInitPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        capture_pageview: true,
        capture_pageleave: true,
        person_profiles: 'identified_only',
      });

      analyticsClient = posthog;
      analyticsReady = true;
      flushPendingOperations();
      return posthog;
    })
    .catch((error) => {
      analyticsInitPromise = null;
      pendingOperations.length = 0;
      throw error;
    });

  return analyticsInitPromise;
}

export function initAnalytics() {
  if (!isBrowser || !hasPostHogConfig || analyticsLoadScheduled) return;

  analyticsLoadScheduled = true;

  const startLoading = () => {
    void loadPostHogClient();
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(startLoading, { timeout: 2000 });
    return;
  }

  window.setTimeout(startLoading, 1200);
}

export function trackEvent(eventName, properties = {}) {
  if (!isBrowser) return;

  if (queueOperation((client) => client.capture(eventName, properties))) {
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, properties);
    return;
  }

  window.dispatchEvent(
    new CustomEvent('app:analytics', {
      detail: { eventName, properties },
    }),
  );
}

export function identifyAnalyticsUser(session) {
  if (!session?.user?.id) {
    resetAnalyticsIdentity();
    return;
  }

  queueOperation((client) => {
    client.identify(String(session.user.id), {
      email: session.user.email,
      name: session.user.name,
      role: session.role,
      barbershop_id: session.barbershopId,
      is_active: session.isActive,
      is_organization_owner: session.isOrganizationOwner,
    });
  });
}

export function resetAnalyticsIdentity() {
  queueOperation((client) => {
    client.reset();
  });
}

export function trackNavigationIntent({
  source,
  trigger,
  from,
  to,
  label,
}) {
  if (!isBrowser) return;

  const payload = {
    source,
    trigger,
    from,
    to,
    label,
    timestamp: Date.now(),
  };

  window.sessionStorage.setItem(
    PENDING_NAVIGATION_KEY,
    JSON.stringify(payload),
  );

  trackEvent('navigation_intent', {
    navigation_source: source,
    navigation_trigger: trigger,
    from,
    to,
    label,
  });
}

export function consumePendingNavigation(pathname) {
  if (!isBrowser) return null;

  const rawPayload = window.sessionStorage.getItem(PENDING_NAVIGATION_KEY);
  if (!rawPayload) return null;

  try {
    const payload = JSON.parse(rawPayload);
    if (payload?.to !== pathname) {
      return null;
    }

    window.sessionStorage.removeItem(PENDING_NAVIGATION_KEY);
    return payload;
  } catch {
    window.sessionStorage.removeItem(PENDING_NAVIGATION_KEY);
    return null;
  }
}

export function isAnalyticsReady() {
  return analyticsReady;
}
